import Dialog from '@mui/material/Dialog';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useUpdateUsersMutation } from '@/api/v1';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { EditUserForm } from '@/components/organisms/EditUserForm';
import { UpdateUserFormData, UpdateUserRequest } from '@/DTOs/users';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';

export const EditUserModal: React.FC = (): React.ReactElement => {
  const { userId } = useParams();

  const [updateUsers] = useUpdateUsersMutation();

  const toast = useToast();
  const tr = useTranslator();
  const navigate = useNavigate();

  const onClose = React.useCallback((): void => {
    navigate('..');
  }, [navigate]);

  const onUpdateUser = React.useCallback(
    async (formData: UpdateUserFormData): Promise<void> => {
      if (!userId) {
        toast.show('User ID is missing', 'error');
        return;
      }

      const updateUserRequest: UpdateUserRequest = {
        id: userId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        group: formData.group,
        department: formData.department,
      };

      try {
        await updateUsers(updateUserRequest).unwrap();

        toast.show('User updated successfully', 'success');
        onClose();
      } catch (error) {
        console.error('Error updating user:', error);
        toast.show('Error updating user', 'error');
      }
    },
    [userId, updateUsers, onClose, toast],
  );

  React.useEffect((): VoidFunction => {
    return (): void => {
      toast.remove();
    };
  }, [toast]);

  return (
    <Dialog open={true}>
      <ApplicationModal title={tr('MANAGE_USER')} variant="prominent" onClose={onClose}>
        <EditUserForm userId={userId} onSubmit={onUpdateUser} />
      </ApplicationModal>
    </Dialog>
  );
};
